import apiClient from "@utils/axiosInstance";

// LWI-1549 — API surface for the MSR wizard. Section saves hit the per-section
// endpoints added in iq_api (MsrSectionController); Pick and Pack keeps its
// existing endpoint. Every function returns `response.data`.

export const fetchMsrNumber = async () =>
  (await apiClient.get("msr-number")).data;

export const fetchMsr = async (msrNumber) =>
  (await apiClient.get(`msr/${encodeURIComponent(msrNumber)}`)).data;

export const fetchPickAndPack = async (shipmentId) =>
  (await apiClient.get(`msr/getpickandpack/${shipmentId}`)).data;

export const fetchHandlingUnits = async (shipmentId) =>
  (await apiClient.get(`msr/gethandlingparts/${shipmentId}`)).data;

export const fetchCustomsInvoice = async (shipmentId) =>
  (await apiClient.get(`msr/customs/${shipmentId}`)).data;

export const createCustomsInvoice = async (shipmentId) =>
  (await apiClient.post("customs", { parent_id: shipmentId })).data;

/** POST msr/{number} — creates the record (draft or submitted) from the flat Create payload. */
export const createMsr = async (msrNumber, flatPayload) =>
  (await apiClient.post(`msr/${encodeURIComponent(msrNumber)}`, flatPayload))
    .data;

/** PUT msr/{number}/attributes — any subset of header keys; parts + status untouched. */
export const saveMsrAttributes = async (msrNumber, header) =>
  (
    await apiClient.put(
      `msr/${encodeURIComponent(msrNumber)}/attributes`,
      header
    )
  ).data;

/** PUT msr/{number}/parts — the full parts list for the MSR. */
export const saveMsrParts = async (msrNumber, parts) =>
  (await apiClient.put(`msr/${encodeURIComponent(msrNumber)}/parts`, { parts }))
    .data;

/** POST msr/{number}/copy — LWI-1377 copy into a new draft; returns `{ msr_number }`. */
export const copyMsr = async (msrNumber, withItems) =>
  (
    await apiClient.post(`msr/${encodeURIComponent(msrNumber)}/copy`, {
      withItems,
    })
  ).data;

/**
 * GET msr/{number}/shipper-pdf — the one-page shipper the warehouse picks from
 * (LWI-1535). Rendered fresh on every call, so the sheet always matches the MSR.
 */
export const fetchShipperPdf = async (msrNumber) =>
  (
    await apiClient.get(`msr/${encodeURIComponent(msrNumber)}/shipper-pdf`, {
      responseType: "blob",
    })
  ).data;

/**
 * GET msr/{number}/packing-list-pdf — the Packing List for an MSR at the carrier
 * (LWI-1706). Rendered fresh on every call; 422 {message} before pickup or when cancelled.
 */
export const fetchPackingListPdf = async (msrNumber) =>
  (
    await apiClient.get(
      `msr/${encodeURIComponent(msrNumber)}/packing-list-pdf`,
      { responseType: "blob" }
    )
  ).data;

/** POST msr/{number}/submit — Draft → Pick and Pack after full validation (422 per section). */
export const submitMsr = async (msrNumber) =>
  (await apiClient.post(`msr/${encodeURIComponent(msrNumber)}/submit`)).data;

/** GET location/{code} — one mysql.location row (profile default Ship From, LWI-1143). */
export const fetchLocation = async (code) =>
  (await apiClient.get(`location/${encodeURIComponent(code)}`)).data;

/** GET msr/{number}/bol — TMS carrier/tracking (`tms`, null until booked) + manual shipping details. */
export const fetchMsrBol = async (msrNumber) =>
  (await apiClient.get(`msr/${encodeURIComponent(msrNumber)}/bol`)).data;

/**
 * GET msr/{number}/route (LWI-1664) - `{ booked: false }` until the MSR is in the
 * TMS, then the ISR route shape (stops, legs, events, summary). A TMS mirror
 * failure is a 503.
 */
export const fetchMsrRoute = async (msrNumber) =>
  (await apiClient.get(`msr/${encodeURIComponent(msrNumber)}/route`)).data;

/**
 * PUT msr/pickandpack/{msrNumber} — existing endpoint, keyed by the MSR number
 * (the numeric shipment id goes in the body as `shipment_id`). `status` 2 =
 * Save Pick and Pack, 3 = Submit Pick and Pack (carrier); omitted = save
 * fields only.
 */
export const savePickAndPack = async (msrNumber, payload) =>
  (await apiClient.put(`msr/pickandpack/${encodeURIComponent(msrNumber)}`, payload)).data;

// LWI-1572 — the MercuryGate push log for an MSR (newest first) and a resend.
// A resend of an MSR already sent is a 409 unless `force` is set (that would
// create a second shipment in the TMS).
export const fetchTmsPushes = async (msrNumber) =>
  (await apiClient.get(`msr/${encodeURIComponent(msrNumber)}/tms-pushes`)).data;

export const resendToTms = async (msrNumber, force = false) =>
  (await apiClient.post(`msr/${encodeURIComponent(msrNumber)}/resend-tms`, { force })).data;

const dropdown = (field) =>
  apiClient.get(`dropdown_fields/${field}/values`).then((r) => r.data || []);

/** Every dropdown the Draft and Pick and Pack tabs need, in one round of requests. */
export const fetchMsrOptions = async () => {
  const [
    incoTerms,
    currencies,
    chargeTypes,
    reasons,
    priorities,
    projects,
    programs,
    shipVia,
    shippingMethods,
    authorizedNames,
  ] = await Promise.all(
    [
      dropdown("inco_terms"),
      dropdown("currency"),
      dropdown("charge_type"),
      dropdown("reason_for_shipment"),
      dropdown("shipment_priorities"),
      apiClient.get("projects").then((r) => r.data || []),
      apiClient.get("programs").then((r) => r.data || []),
      dropdown("ship_via"),
      dropdown("shipping_method"),
      dropdown("authorized_name"),
    ].map((p) => p.catch(() => []))
  );
  return {
    incoTerms,
    currencies,
    chargeTypes,
    reasons,
    priorities,
    projects,
    programs,
    shipVia,
    shippingMethods,
    authorizedNames,
  };
};

// LWI-1644 — exceptions. Rows carry `meta` (what clears each one); resolve and
// reopen answer {message, exception, msr_number, open_count}.

/** GET msr/exceptions/{shipmentId} — the MSR's exception rows (with `meta`). */
export const fetchMsrExceptions = async (shipmentId) => {
  const data = (await apiClient.get(`msr/exceptions/${shipmentId}`)).data;
  return Array.isArray(data) ? data : [];
};

/** POST msr/exception-check/{shipmentId} — re-runs the rules (drafts are skipped server-side). */
export const recheckMsrExceptions = async (shipmentId) =>
  (await apiClient.post(`msr/exception-check/${shipmentId}`)).data;

/** PUT msr/exceptions/{exceptionId}/resolve — {reason}, required, 300 max. */
export const resolveMsrException = async (exceptionId, reason) =>
  (await apiClient.put(`msr/exceptions/${exceptionId}/resolve`, { reason }))
    .data;

/** PUT msr/exceptions/{exceptionId}/reopen — only for a row resolved by hand. */
export const reopenMsrException = async (exceptionId) =>
  (await apiClient.put(`msr/exceptions/${exceptionId}/reopen`)).data;
